import { useState, useEffect, useRef, useCallback } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { MISSED_INTERACTION_DATE_LOOKBACK_DAYS, getLastBusinessDay, getTwoBusinessDaysAgo } from "../constants";
import { InteractionOrder, Interaction } from "../types/interactions";
import { getInteractionKey } from "../utils/interactionKey";

const isAbortError = (err: unknown): boolean => {
  if ((axios as any).isCancel?.(err)) return true;
  if ((err as any)?.code === "ERR_CANCELED") return true;
  if (typeof (err as any)?.message === "string" && (err as any).message.toLowerCase().includes("canceled")) return true;
  return (err instanceof Error && err.name === "CanceledError") || (err instanceof DOMException && err.name === "AbortError");
};

interface UseMissedInteractionOrdersReturn {
  orders: InteractionOrder[];
  isLoading: boolean;
  error: string | null;
  existingInteractions: Record<string, any>;
  existingSkippedInteractions: Record<string, any>;
  initialInteractions: Record<string, Interaction>;
  abort: () => void;
}
export const useMissedInteractionOrders = (
  baseStatus: "Closed" | "Received",
  isActive: boolean
): UseMissedInteractionOrdersReturn => {
  const [orders, setOrders] = useState<InteractionOrder[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [existingInteractions, setExistingInteractions] = useState<Record<string, any>>({});
  const [existingSkippedInteractions, setExistingSkippedInteractions] = useState<Record<string, any>>({});
  const [initialInteractions, setInitialInteractions] = useState<Record<string, Interaction>>({});
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<string | null>(null);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsLoading(false);
    setError(null);
  }, []);

  useEffect(() => {
    if (!isActive) {
      setIsLoading(false);
      setError(null);
      setOrders([]);
      setExistingInteractions({});
      setExistingSkippedInteractions({});
      setInitialInteractions({});
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const requestId = crypto.randomUUID();
    requestIdRef.current = requestId;

    const fetchMissedOrders = async () => {
      setIsLoading(true);
      setError(null);
      setOrders([]);
      setExistingInteractions({});
      setExistingSkippedInteractions({});
      setInitialInteractions({});

      try {
        const regularTabDate = baseStatus === "Closed" ? getLastBusinessDay() : getTwoBusinessDaysAgo();
        const toDate = regularTabDate.subtract(1, "day");
        const fromDate = toDate.subtract(MISSED_INTERACTION_DATE_LOOKBACK_DAYS, "day");
        const fromDateInt = parseInt(fromDate.format("YYYYMMDD"));
        const toDateInt = parseInt(toDate.format("YYYYMMDD"));

        const endpoint =
          baseStatus === "Closed"
            ? "/api/interactions/GetClosedOrdersDateRange"
            : "/api/interactions/GetReceivedOrdersDateRange";

        const response = await axios.get<InteractionOrder[]>(endpoint, {
          params: { fromDate: fromDateInt, toDate: toDateInt },
          signal: abortController.signal
        });

        let data = response.data;

        const seenKeys = new Set<string>();
        data = data.filter((order) => {
          if (!order.orderNumber || !order.phonebookContactId) return false;
          const key = getInteractionKey(order.orderNumber, order.phonebookContactId);
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        });

        const orderContactTuples = data.map((order) => ({
          orderNumber: order.orderNumber!,
          contactId: order.phonebookContactId!
        }));

        let existingInteractionsData: any[] = [];
        let existingSkippedInteractionsData: any[] = [];
        let preInstructionsOnlyData: any[] = [];

        if (orderContactTuples.length > 0) {
          try {
            const { data: allInteractions } = await axios.post<any[]>(
              "/api/clientphonebook/contactInteractions/getByOrderNumbers",
              orderContactTuples,
              { signal: abortController.signal }
            );

            const isReceivedOnClosedTab = (i: any) =>
              baseStatus === "Closed" && i.interactionCategory === "Received";

            existingInteractionsData = allInteractions.filter(
              (i) => i.typeId != null && i.typeId !== 10 && !isReceivedOnClosedTab(i)
            );
            preInstructionsOnlyData = allInteractions.filter(
              (i) => i.typeId == null && !isReceivedOnClosedTab(i)
            );
            existingSkippedInteractionsData = allInteractions
              .filter((i) => i.typeId === 10 && !isReceivedOnClosedTab(i))
              .map((i) => ({
                id: i.id,
                orderNumber: i.orderNumber,
                contactId: i.contactId,
                skipReason: i.notes,
                skippedDate: i.interactionDate,
                skippedByUserId: i.clientCultivatorId,
                createdAt: i.createdAt,
                updatedAt: i.updatedAt
              }));
          } catch (err) {
            if (isAbortError(err)) throw err;
          }
        }

        const existingMap: Record<string, any> = {};
        existingInteractionsData.forEach((interaction) => {
          if (interaction.orderNumber && interaction.contactId) {
            const key = getInteractionKey(interaction.orderNumber, interaction.contactId);
            existingMap[key] = interaction;
          }
        });
        setExistingInteractions(existingMap);

        const existingSkippedMap: Record<string, any> = {};
        existingSkippedInteractionsData.forEach((skipped) => {
          if (skipped.orderNumber && skipped.contactId) {
            const key = getInteractionKey(skipped.orderNumber, skipped.contactId);
            existingSkippedMap[key] = skipped;
          }
        });
        setExistingSkippedInteractions(existingSkippedMap);

        const preInstructionsOnlyMap: Record<string, any> = {};
        preInstructionsOnlyData.forEach((i) => {
          if (i.orderNumber && i.contactId) {
            const key = getInteractionKey(i.orderNumber, i.contactId);
            preInstructionsOnlyMap[key] = i;
          }
        });

        const missedData = data.filter((order) => {
          const key = getInteractionKey(order.orderNumber!, order.phonebookContactId!);
          return !existingMap[key] && !existingSkippedMap[key];
        });

        let pastInteractionsCounts: Record<string, number> = {};
        if (missedData.length > 0) {
          try {
            const uniqueContactIds = [
              ...new Set(missedData.map((order) => order.phonebookContactId!).filter((contactId) => contactId > 0))
            ];
            const contactCountsMap = new Map<number, number>();

            const countPromises = uniqueContactIds.map(async (contactId) => {
              try {
                const { data: pastInteractions } = await axios.get(
                  "/api/clientphonebook/contactInteractions/getPastInteractions",
                  {
                    params: { contactId },
                    signal: abortController.signal
                  }
                );
                return { contactId, count: pastInteractions.length };
              } catch (err) {
                if (isAbortError(err)) throw err;
                return { contactId, count: 0 };
              }
            });

            const counts = await Promise.all(countPromises);
            counts.forEach(({ contactId, count }) => {
              contactCountsMap.set(contactId, count);
            });

            missedData.forEach((order) => {
              const key = getInteractionKey(order.orderNumber!, order.phonebookContactId!);
              pastInteractionsCounts[key] = contactCountsMap.get(order.phonebookContactId!) || 0;
            });
          } catch (err) {
            if (isAbortError(err)) throw err;
            console.error("Error fetching past interactions counts:", err);
          }
        }

        const ordersWithMeta = missedData
          .map((order) => {
            const key = getInteractionKey(order.orderNumber!, order.phonebookContactId!);
            const existingSkipped = existingSkippedMap[key];
            const preInstructionsOnly = preInstructionsOnlyMap[key];
            const preInstructions =
              existingSkipped?.preInteractionInstructions ?? preInstructionsOnly?.preInteractionInstructions ?? null;
            return {
              ...order,
              preInteractionInstructions: preInstructions,
              preInteractionInstructionsBy: preInstructions ? (preInstructionsOnly?.clientCultivator ?? null) : null,
              preInteractionInstructionsAt: preInstructions ? (preInstructionsOnly?.updatedAt ?? null) : null,
              pastInteractionsCount: pastInteractionsCounts[key] || 0,
              isClosedConfirmed: false as boolean,
              closedConfirmedByUserId: null as number | null,
              closedConfirmedAt: null as string | null,
              closedConfirmedByUser: null as { id: number; firstName: string; lastName: string } | null
            };
          })
          .sort((a, b) => {
            const dateA = baseStatus === "Closed" ? a.settlementDate : a.receivedDate;
            const dateB = baseStatus === "Closed" ? b.settlementDate : b.receivedDate;
            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;
            return dateB.localeCompare(dateA);
          });

        if (baseStatus === "Closed") {
          const uniqueOrderNumbers = [...new Set(ordersWithMeta.map((o) => o.orderNumber).filter(Boolean))] as string[];
          if (uniqueOrderNumbers.length > 0) {
            try {
              const { data: confirmedOrders } = await axios.post<
                {
                  orderNumber: string;
                  confirmedByUserId: number;
                  confirmedAt: string;
                  confirmedByUser: { id: number; firstName: string; lastName: string };
                }[]
              >("/api/clientphonebook/confirmedClosedOrders/getByOrderNumbers", uniqueOrderNumbers, {
                signal: abortController.signal
              });

              const confirmedMap = new Map(confirmedOrders.map((confirmed) => [confirmed.orderNumber, confirmed]));
              ordersWithMeta.forEach((order) => {
                if (!order.orderNumber) return;

                const confirmed = confirmedMap.get(order.orderNumber);
                if (!confirmed) return;

                order.isClosedConfirmed = true;
                order.closedConfirmedByUserId = confirmed.confirmedByUserId;
                order.closedConfirmedAt = confirmed.confirmedAt;
                order.closedConfirmedByUser = confirmed.confirmedByUser;
              });
            } catch (err) {
              if (isAbortError(err)) throw err;
            }
          }
        }

        if (abortController.signal.aborted || abortControllerRef.current?.signal.aborted || requestIdRef.current !== requestId) return;
        setOrders(ordersWithMeta);
        setInitialInteractions({});
      } catch (err) {
        if (isAbortError(err) || requestIdRef.current !== requestId) return;
        setError("Failed to load missed interaction orders");
        setOrders([]);
      } finally {
        if (!abortController.signal.aborted && requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    };

    fetchMissedOrders();

    return () => {
      abortController.abort();
    };
  }, [baseStatus, isActive]);

  return {
    orders,
    isLoading,
    error,
    existingInteractions,
    existingSkippedInteractions,
    initialInteractions,
    abort
  };
};
